import {css} from "@panda/css";

import {BrandPhoneIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {formatDate, numberFormat, phoneNumberFormat} from "@pg-mono/string-utils";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {BRAND_NAME} from "../../../../app/constants/company";
import {HOTLINE_VENDOR_REPORT_PHONE_NUMBER} from "../../../../hotline/constants/hotline_phone_numbers";
import {OfferDisplayType} from "../../../../offer/helpers/OfferDisplayType";
import {useAppDispatch, useAppSelector} from "../../../../store/hooks";
import {useGetVendorDetailQuery} from "../../../api/get_vendor_detail_api";
import {closeVendorReportModal, selectVendorReportModalId} from "../../../state/vendor_detail_slice";
import {IVendorDetail} from "../../../types/IVendorDetail";
import {getRecommendationStat} from "../VendorDetailRecommendation";
import {VendorDetailReportModalContentAwards} from "./VendorDetailReportModalContentAwards";
import {VendorDetailReportModalContentDescription} from "./VendorDetailReportModalContentDescription";
import {VendorDetailReportModalContentOffersTable} from "./VendorDetailReportModalContentOffersTable";
import {VendorDetailReportModalContentPromotions} from "./VendorDetailReportModalContentPromotions";

const rpLogo = require("../../../../app/images/rp_logo.svg");

export function VendorDetailReportModal() {
    const dispatch = useAppDispatch();
    const vendorReportModalId = useAppSelector(selectVendorReportModalId);
    const isOpen = typeof vendorReportModalId === "number";
    const {data: vendor} = useGetVendorDetailQuery(vendorReportModalId as number, {skip: !isOpen});

    const handleClose = () => {
        dispatch(closeVendorReportModal());
    };

    return (
        <Modal fullScreen isOpen={isOpen} onModalClose={handleClose} className={modalStyle}>
            <Modal.CloseButton placement="right" className={closeButtonStyle} />
            <Modal.Content className={modalContentStyle}>
                {!vendor && <Loader size="xl" />}
                {vendor && <VendorDetailReportModalContent vendor={vendor} />}
            </Modal.Content>
        </Modal>
    );
}

function VendorDetailReportModalContent(props: {vendor: IVendorDetail}) {
    const {vendor} = props;
    const {isMobile} = useUserDevice();

    const currentDate = formatDate(new Date().toISOString(), "dd.MM.yyyy");
    const variables = useAppSelector((state) => state.variables);
    const stats = vendor.recommendation?.stats
        ? vendor.recommendation.stats
              .map((stat) => getRecommendationStat(vendor, variables, stat.label))
              .filter((stat) => stat && stat.label !== "Lat obecności na GPW")
        : [];

    const handlers = {
        handlePhoneClick: () => {
            window.open(`tel:${HOTLINE_VENDOR_REPORT_PHONE_NUMBER}`, "_self");
        }
    };

    return (
        <>
            <div className={headerStyle}>
                <a href={logoData.url} className={logoStyle}>
                    <Image
                        src={logoData.imgUrl}
                        alt={logoData.alt}
                        width={isMobile ? "78" : "156"}
                        height={isMobile ? "24" : "48"}
                        data-testid={NAVIGATION_TEST_IDS.MAIN_LOGO}
                    />
                </a>
                <Text variant={isMobile ? "headline_6" : "headline_3"}>Raport o deweloperze</Text>
                <Text variant="body_copy_2" className={dateStyle}>
                    {currentDate}
                </Text>
            </div>
            <div className={bodyContentStyle}>
                <VendorDetailReportModalContentDescription vendor={vendor} />
                <div className={statsContainerStyle}>
                    {stats &&
                        stats.length > 0 &&
                        stats.map(
                            (stat) =>
                                stat && (
                                    <Text key={stat.label} className={statStyle} variant="headline_6">
                                        {numberFormat(stat.value)} {stat.label.toLowerCase()}
                                    </Text>
                                )
                        )}
                </div>
                <VendorDetailReportModalContentAwards vendor={vendor} />
                {vendor.stats.offers_count_sold > 0 && (
                    <VendorDetailReportModalContentOffersTable vendor={vendor} type={OfferDisplayType.SOLD} header="ZREALIZOWANE INWESTYCJE" />
                )}
                {vendor.stats.offers_count_for_sale > 0 && (
                    <VendorDetailReportModalContentOffersTable vendor={vendor} type={OfferDisplayType.FOR_SALE} header="INWESTYCJE AKTUALNIE W SPRZEDAŻY" />
                )}
                <VendorDetailReportModalContentPromotions vendor={vendor} />
                <div className={hotlineContainerStyle}>
                    <div className={hotlineHeaderStyle}>
                        {!isMobile && <BrandPhoneIcon size="4" className={phoneIconStyle} />}
                        <div className={hotlineInfoStyle}>
                            <Text variant="headline_6">Skorzystaj z usługi naszego doradcy mieszkaniowego </Text>
                            <Text variant="body_copy_2" mb={2}>
                                Bezpłatne znajdziemy dla Ciebie mieszkanie
                            </Text>
                            <Text variant={isMobile ? "headline_6" : "headline_3"} onClick={isMobile ? handlers.handlePhoneClick : undefined}>
                                {phoneNumberFormat(HOTLINE_VENDOR_REPORT_PHONE_NUMBER)}
                            </Text>
                        </div>
                    </div>
                    <Image src={logoData.imgUrl} alt={logoData.alt} width={isMobile ? "104" : "156"} height={isMobile ? "32" : "48"} />
                </div>
            </div>
        </>
    );
}

// Styles
const logoData = {
    imgUrl: rpLogo,
    url: rpAppLink.base(),
    alt: `Logo portalu ${BRAND_NAME}`,
    title: `Logo portalu ${BRAND_NAME}`
};

const modalStyle = css({
    py: 2
});

const headerStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    pb: 4,
    gap: 4,
    mb: 3,
    borderColor: "var(--colors-gray-600)",
    borderBottom: "solid 1px",
    justifyContent: "space-between",

    md: {
        gap: 0,
        mb: 6
    }
});

const dateStyle = css({
    borderColor: "var(--colors-gray-600)",
    border: "solid 1px",
    display: "flex",
    p: 1,
    mr: 2,
    justifyContent: "center",
    alignItems: "center",

    md: {
        mr: 8,
        p: 2
    }
});

const bodyContentStyle = css({
    px: 2,

    sm: {
        px: 6
    },

    md: {
        maxWidth: "var(--grid-md-widthPx)",
        ml: "auto",
        mr: "auto"
    },

    lg: {
        maxWidth: "var(--grid-lg-widthPx)",
        ml: "auto",
        mr: "auto"
    }
});

const modalContentStyle = css({
    p: 0,
    pt: 2
});

const logoStyle = css({
    ml: 2,

    md: {
        ml: 6
    }
});

const statStyle = css({
    border: "solid 1px var(--colors-gray-600)",
    py: 1,
    px: 2,

    md: {
        py: 3,
        px: 4
    }
});

const statsContainerStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 2,
    flexWrap: "wrap",
    mb: 6,

    md: {
        mb: 10,
        gap: 4
    }
});

const hotlineContainerStyle = css({
    display: "flex",
    flexDirection: "row",
    p: 1,
    mt: 6,
    border: "solid 1px var(--colors-gray-600)",
    alignItems: "center",
    mb: 3,
    justifyContent: "space-between",

    md: {
        mt: 10,
        mb: 6,
        p: 3
    }
});

const hotlineInfoStyle = css({
    md: {
        ml: 6
    }
});

const hotlineHeaderStyle = css({
    display: "flex",
    flexDirection: "row",
    maxWidth: "65%",

    md: {
        maxWidth: "unset"
    }
});

const phoneIconStyle = css({
    position: "absolute",

    md: {
        mt: 2,
        ml: 1
    }
});

const closeButtonStyle = css({
    md: {
        mr: 2
    }
});
import {css} from "@panda/css";

import {BrandPhoneIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {formatDate, numberFormat, phoneNumberFormat} from "@pg-mono/string-utils";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {BRAND_NAME} from "../../../../app/constants/company";
import {HOTLINE_VENDOR_REPORT_PHONE_NUMBER} from "../../../../hotline/constants/hotline_phone_numbers";
import {OfferDisplayType} from "../../../../offer/helpers/OfferDisplayType";
import {useAppDispatch, useAppSelector} from "../../../../store/hooks";
import {useGetVendorDetailQuery} from "../../../api/get_vendor_detail_api";
import {closeVendorReportModal, selectVendorReportModalId} from "../../../state/vendor_detail_slice";
import {IVendorDetail} from "../../../types/IVendorDetail";
import {getRecommendationStat} from "../VendorDetailRecommendation";
import {VendorDetailReportModalContentAwards} from "./VendorDetailReportModalContentAwards";
import {VendorDetailReportModalContentDescription} from "./VendorDetailReportModalContentDescription";
import {VendorDetailReportModalContentOffersTable} from "./VendorDetailReportModalContentOffersTable";
import {VendorDetailReportModalContentPromotions} from "./VendorDetailReportModalContentPromotions";

const rpLogo = require("../../../../app/images/rp_logo.svg");

export function VendorDetailReportModal() {
    const dispatch = useAppDispatch();
    const vendorReportModalId = useAppSelector(selectVendorReportModalId);
    const isOpen = typeof vendorReportModalId === "number";
    const {data: vendor} = useGetVendorDetailQuery(vendorReportModalId as number, {skip: !isOpen});

    const handleClose = () => {
        dispatch(closeVendorReportModal());
    };

    return (
        <Modal fullScreen isOpen={isOpen} onModalClose={handleClose} className={modalStyle}>
            <Modal.CloseButton placement="right" className={closeButtonStyle} />
            <Modal.Content className={modalContentStyle}>
                {!vendor && <Loader size="xl" />}
                {vendor && <VendorDetailReportModalContent vendor={vendor} />}
            </Modal.Content>
        </Modal>
    );
}

function VendorDetailReportModalContent(props: {vendor: IVendorDetail}) {
    const {vendor} = props;
    const {isMobile} = useUserDevice();

    const currentDate = formatDate(new Date().toISOString(), "dd.MM.yyyy");
    const variables = useAppSelector((state) => state.variables);
    const stats = vendor.recommendation?.stats
        ? vendor.recommendation.stats
              .map((stat) => getRecommendationStat(vendor, variables, stat.label))
              .filter((stat) => stat && stat.label !== "Lat obecności na GPW")
        : [];

    const handlers = {
        handlePhoneClick: () => {
            window.open(`tel:${HOTLINE_VENDOR_REPORT_PHONE_NUMBER}`, "_self");
        }
    };

    return (
        <>
            <div className={headerStyle}>
                <a href={logoData.url} className={logoStyle}>
                    <Image
                        src={logoData.imgUrl}
                        alt={logoData.alt}
                        width={isMobile ? "78" : "156"}
                        height={isMobile ? "24" : "48"}
                        data-testid={NAVIGATION_TEST_IDS.MAIN_LOGO}
                    />
                </a>
                <Text variant={isMobile ? "headline_6" : "headline_3"}>Raport o deweloperze</Text>
                <Text variant="body_copy_2" className={dateStyle}>
                    {currentDate}
                </Text>
            </div>
            <div className={bodyContentStyle}>
                <VendorDetailReportModalContentDescription vendor={vendor} />
                <div className={statsContainerStyle}>
                    {stats &&
                        stats.length > 0 &&
                        stats.map(
                            (stat) =>
                                stat && (
                                    <Text key={stat.label} className={statStyle} variant="headline_6">
                                        {numberFormat(stat.value)} {stat.label.toLowerCase()}
                                    </Text>
                                )
                        )}
                </div>
                <VendorDetailReportModalContentAwards vendor={vendor} />
                {vendor.stats.offers_count_sold > 0 && (
                    <VendorDetailReportModalContentOffersTable vendor={vendor} type={OfferDisplayType.SOLD} header="ZREALIZOWANE INWESTYCJE" />
                )}
                {vendor.stats.offers_count_for_sale > 0 && (
                    <VendorDetailReportModalContentOffersTable vendor={vendor} type={OfferDisplayType.FOR_SALE} header="INWESTYCJE AKTUALNIE W SPRZEDAŻY" />
                )}
                <VendorDetailReportModalContentPromotions vendor={vendor} />
                <div className={hotlineContainerStyle}>
                    <div className={hotlineHeaderStyle}>
                        {!isMobile && <BrandPhoneIcon size="4" className={phoneIconStyle} />}
                        <div className={hotlineInfoStyle}>
                            <Text variant="headline_6">Skorzystaj z usługi naszego doradcy mieszkaniowego </Text>
                            <Text variant="body_copy_2" mb={2}>
                                Bezpłatne znajdziemy dla Ciebie mieszkanie
                            </Text>
                            <Text variant={isMobile ? "headline_6" : "headline_3"} onClick={isMobile ? handlers.handlePhoneClick : undefined}>
                                {phoneNumberFormat(HOTLINE_VENDOR_REPORT_PHONE_NUMBER)}
                            </Text>
                        </div>
                    </div>
                    <Image src={logoData.imgUrl} alt={logoData.alt} width={isMobile ? "104" : "156"} height={isMobile ? "32" : "48"} />
                </div>
            </div>
        </>
    );
}

// Styles
const logoData = {
    imgUrl: rpLogo,
    url: rpAppLink.base(),
    alt: `Logo portalu ${BRAND_NAME}`,
    title: `Logo portalu ${BRAND_NAME}`
};

const modalStyle = css({
    py: 2
});

const headerStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    pb: 4,
    gap: 4,
    mb: 3,
    borderColor: "var(--colors-gray-600)",
    borderBottom: "solid 1px",
    justifyContent: "space-between",

    md: {
        gap: 0,
        mb: 6
    }
});

const dateStyle = css({
    borderColor: "var(--colors-gray-600)",
    border: "solid 1px",
    display: "flex",
    p: 1,
    mr: 2,
    justifyContent: "center",
    alignItems: "center",

    md: {
        mr: 8,
        p: 2
    }
});

const bodyContentStyle = css({
    px: 2,

    sm: {
        px: 6
    },

    md: {
        maxWidth: "var(--grid-md-widthPx)",
        ml: "auto",
        mr: "auto"
    },

    lg: {
        maxWidth: "var(--grid-lg-widthPx)",
        ml: "auto",
        mr: "auto"
    }
});

const modalContentStyle = css({
    p: 0,
    pt: 2
});

const logoStyle = css({
    ml: 2,

    md: {
        ml: 6
    }
});

const statStyle = css({
    border: "solid 1px var(--colors-gray-600)",
    py: 1,
    px: 2,

    md: {
        py: 3,
        px: 4
    }
});

const statsContainerStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 2,
    flexWrap: "wrap",
    mb: 6,

    md: {
        mb: 10,
        gap: 4
    }
});

const hotlineContainerStyle = css({
    display: "flex",
    flexDirection: "row",
    p: 1,
    mt: 6,
    border: "solid 1px var(--colors-gray-600)",
    alignItems: "center",
    mb: 3,
    justifyContent: "space-between",

    md: {
        mt: 10,
        mb: 6,
        p: 3
    }
});

const hotlineInfoStyle = css({
    md: {
        ml: 6
    }
});

const hotlineHeaderStyle = css({
    display: "flex",
    flexDirection: "row",
    maxWidth: "65%",

    md: {
        maxWidth: "unset"
    }
});

const phoneIconStyle = css({
    position: "absolute",

    md: {
        mt: 2,
        ml: 1
    }
});

const closeButtonStyle = css({
    md: {
        mr: 2
    }
});
